import { ReactiveController, ReactiveControllerHost } from 'lit';

export default class CollapsiblePanelController<T extends HTMLElement> implements ReactiveController {
  private host: ReactiveControllerHost & Element;
  private _openPanels = new Set<T>();

  private allPanels: NodeListOf<HTMLElement> | null = null;
  /**
   * This flag is used for controlling the expand/collapse behavior for all panels
   * @private
   */
  private allowMultipleOpen = false;

  constructor(host: ReactiveControllerHost & Element, allowMultipleOpen = false) {
    this.host = host;
    this.allowMultipleOpen = allowMultipleOpen;
    this.host.addController(this);
    this.setInitialOpenPanel();
  }

  hostUpdated(): void {
    // Query all `<gz-collapsible-panel>` elements in the host's shadowRoot, then cast or map them to type T.
    this.allPanels = this.host.shadowRoot?.querySelectorAll('gz-collapsible-panel') ?? null;
  }

  private setInitialOpenPanel(): void {
    this.host.updateComplete.then(() => {
      if (!this.allPanels) {
        return;
      }

      // check the DOM for declarative open panels or get the first panel and set it to open
      const allOpenPanels = this.host.shadowRoot?.querySelectorAll('[isOpen]');
      if (allOpenPanels && allOpenPanels.length > 0) {
        allOpenPanels.forEach(panel => this._openPanels.add(panel as T));
      } else {
        const firstPanel = this.allPanels[0] as T;
        if (firstPanel) {
          this._openPanels.add(firstPanel);
          firstPanel.setAttribute('isOpen', '');
        }
      }
    });
  }

  public setAllowMultipleOpen(allowMultipleOpen: boolean): void {
    this.allowMultipleOpen = allowMultipleOpen;
    this.host.requestUpdate();
  }

  public openPanel(panel: T): void {
    if (!panel) {
      return;
    }

    if (!this.allowMultipleOpen) {
      this._openPanels.forEach(openPanel => openPanel.removeAttribute('isOpen'));
      this._openPanels.clear();
    }

    this._openPanels.add(panel);
    panel.setAttribute('isOpen', '');
  }

  private closePanel(panel: T): void {
    this._openPanels.delete(panel);
    panel.removeAttribute('isOpen');
  }

  public togglePanel(panel: T): void {
    this._openPanels.has(panel) ? this.closePanel(panel) : this.openPanel(panel);
  }

  public expandAllPanels(): void {
    this.updateAllPanels(true);
    this.host.requestUpdate();
  }

  public collapseAllPanels(): void {
    this.updateAllPanels(false);
    this.host.requestUpdate();
  }

  public clearPanels() {
    this._openPanels.clear();
  }

  public resetPanels(): void {
    this.updateAllPanels(false);
    this.setInitialOpenPanel();
    this.host.requestUpdate();
  }

  private updateAllPanels(isOpen: boolean): void {
    // Temporarily allow multiple panels to open
    this.allowMultipleOpen = isOpen;
    this.allPanels?.forEach(panel => {
      if (isOpen) {
        this._openPanels.add(panel as T);
        panel.setAttribute('isOpen', '');
      } else {
        this._openPanels.delete(panel as T);
        panel.removeAttribute('isOpen');
      }
    });
  }
}
